import { useEffect, useMemo, useState } from 'react';
import { History, AlertTriangle, CheckCircle2, Eye } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  UniversalLoadingState,
  type UniversalTableColumn,
  Card,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  StatusBadge,
  NotificationCard,
  type StatusTone,
} from '@afrilink/ui';
import { supabase } from '@/shared/lib/supabase';
import { formatDateTime } from '@/shared/utils/format';

type BulkActionRow = {
  id: string;
  action_kind: string;
  initiated_by: string;
  initiated_by_email: string | null;
  target_count: number;
  succeeded_count: number;
  failed_count: number;
  reason: string;
  dry_run: boolean;
  parameters: Record<string, unknown> | null;
  status: 'running' | 'complete' | 'failed' | 'cancelled' | string;
  started_at: string;
  finished_at: string | null;
  notes: string | null;
};

const ACTION_LABEL: Record<string, string> = {
  send_payment_reminders: 'Payment reminders',
};

const STATUS_TONE: Record<string, StatusTone> = {
  running: 'pending',
  complete: 'active',
  failed: 'failed',
  cancelled: 'draft',
};

function durationMs(start: string, end: string | null): string {
  if (!end) return 'in flight';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

export default function BulkActionsHistoryPage() {
  /**
   * NO FILTER BAR HERE (2026-09-13). `bulk_action_run` holds ZERO rows and
   * always has — verified against prod. A filter bar on a permanently empty
   * table is the inert control F-3 forbids: it implies a list long enough to
   * need narrowing. Add one the day this table has rows worth narrowing.
   */
  const [rows, setRows] = useState<BulkActionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [drawerRow, setDrawerRow] = useState<BulkActionRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('v_admin_bulk_action_runs')
        .select(
          'id, action_kind, initiated_by, initiated_by_email, target_count, succeeded_count, failed_count, reason, dry_run, parameters, status, started_at, finished_at, notes',
        )
        .order('started_at', { ascending: false })
        .limit(200);
      if (cancelled) return;
      if (err) {
        setError(new Error(err.message));
        setLoading(false);
        return;
      }
      setRows((data ?? []) as BulkActionRow[]);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = useMemo<UniversalTableColumn<BulkActionRow>[]>(
    () => [
      {
        key: 'kind',
        header: 'Action',
        sortValue: (r) => r.action_kind,
        render: (r) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">
              {ACTION_LABEL[r.action_kind] ?? r.action_kind}
            </span>
            {r.dry_run && <StatusBadge tone="info">dry-run</StatusBadge>}
          </div>
        ),
      },
      {
        key: 'when',
        header: 'When',
        sortValue: (r) => new Date(r.started_at).getTime(),
        render: (r) => (
          <div className="flex flex-col">
            <span className="text-foreground">{formatDateTime(r.started_at)}</span>
            <span className="text-xs text-muted-foreground">
              took {durationMs(r.started_at, r.finished_at)}
            </span>
          </div>
        ),
      },
      {
        key: 'by',
        header: 'Initiated by',
        sortValue: (r) => r.initiated_by_email ?? '',
        render: (r) => (
          <span className="text-sm text-foreground">
            {r.initiated_by_email ?? r.initiated_by.slice(0, 8) + '…'}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'targets',
        header: 'Targets',
        align: 'right',
        sortValue: (r) => r.target_count,
        render: (r) => <span className="font-medium tabular-nums text-foreground">{r.target_count}</span>,
      },
      {
        key: 'outcome',
        header: 'Outcome',
        render: (r) => (
          <div className="flex flex-col text-xs tabular-nums">
            <span className="text-success">✓ {r.succeeded_count} succeeded</span>
            {r.failed_count > 0 && <span className="text-danger">✗ {r.failed_count} failed</span>}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        render: (r) => <StatusBadge tone={STATUS_TONE[r.status] ?? 'draft'}>{r.status}</StatusBadge>,
        width: 'w-28',
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Bulk action history"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Ops' },
        { label: 'Bulk actions' },
      ]}
    >
      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Every staff-triggered bulk operation (mass reminders, mass suspend, etc.) lands here for
          blast-radius audit per AD-14. Rows are immutable post-finalise. Click a row to see the
          parameters used, error list (if any), and resulting outcomes.
        </p>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load history" body={error.message} />}

      {loading ? (
        <UniversalLoadingState variant="spinner" caption="Loading…" />
      ) : (
        <UniversalTable<BulkActionRow>
          data={rows}
          columns={columns}
          getRowKey={(r) => r.id}
          onRowClick={(r) => setDrawerRow(r)}
          empty={{
            icon: History,
            title: 'No bulk actions yet',
            description:
              'Bulk actions run from /admin/billing → "Send overdue reminders" or future bulk-suspend flows will appear here.',
          }}
        />
      )}

      <Modal
        open={drawerRow !== null}
        onOpenChange={(v) => !v && setDrawerRow(null)}
        title={drawerRow ? `${ACTION_LABEL[drawerRow.action_kind] ?? drawerRow.action_kind} run` : ''}
        description={drawerRow ? `Started ${formatDateTime(drawerRow.started_at)} · ${durationMs(drawerRow.started_at, drawerRow.finished_at)}` : ''}
        size="wide"
      >
        <ModalBody>
          {drawerRow && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Targets" value={drawerRow.target_count} />
                <Stat label="Succeeded" value={drawerRow.succeeded_count} tone="success" />
                <Stat label="Failed" value={drawerRow.failed_count} tone={drawerRow.failed_count > 0 ? 'danger' : 'muted'} />
                <Stat label="Mode" value={drawerRow.dry_run ? 'dry-run' : 'live'} tone="muted" />
              </div>

              <Field label="Reason" value={drawerRow.reason} />
              <Field label="Initiated by" value={drawerRow.initiated_by_email ?? drawerRow.initiated_by} />
              <Field label="Status" value={drawerRow.status} />
              <Field label="Started" value={formatDateTime(drawerRow.started_at)} />
              <Field label="Finished" value={formatDateTime(drawerRow.finished_at)} />

              {drawerRow.parameters && Object.keys(drawerRow.parameters).length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Parameters</p>
                  <pre className="mt-1 overflow-x-auto rounded bg-muted p-3 text-xs">
                    {JSON.stringify(drawerRow.parameters, null, 2)}
                  </pre>
                </div>
              )}

              {drawerRow.notes && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 text-warning" /> Errors / notes
                  </p>
                  <pre className="mt-1 max-h-64 overflow-y-auto rounded border border-danger/30 bg-danger/5 p-3 text-xs text-danger">
                    {drawerRow.notes}
                  </pre>
                </div>
              )}

              {!drawerRow.notes && drawerRow.failed_count === 0 && (
                <div className="inline-flex items-center gap-2 rounded bg-success/5 px-3 py-2 text-xs text-success">
                  <CheckCircle2 className="h-4 w-4" />
                  Completed cleanly with zero failures.
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDrawerRow(null)}>Close</Button>
        </ModalFooter>
      </Modal>
    </UniversalPage>
  );
}

function Stat({
  label,
  value,
  tone = 'muted',
}: {
  label: string;
  value: string | number;
  tone?: 'success' | 'danger' | 'muted';
}) {
  const cls = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-foreground';
  return (
    <div className="rounded border border-border p-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-lg font-semibold tabular-nums ${cls}`}>{value}</p>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="col-span-2">{value}</dd>
    </div>
  );
}

void Eye; // keep import for future quick-view affordance
